import React, { useState } from "react";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc
} from "firebase/firestore";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { useParams } from "react-router-dom";

export default function ExportReports() {
  const { sessionId, themeId } = useParams(); // <-- FIXED

  console.log("🔍 URL Params:", { sessionId, themeId });

  const database = getFirestore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // -----------------------------
  // FETCH THEME
  // -----------------------------
  const fetchTheme = async () => {
    const themePath = `sessions/${sessionId}/themes/${themeId}`;
    console.log("📁 Fetching Theme:", themePath);

    const themeRef = doc(database, themePath);
    const snap = await getDoc(themeRef);

    console.log("📄 Theme exists?", snap.exists());

    return snap.exists() ? snap.data() : null;
  };

  // -----------------------------
  // FETCH ALL REPORTS ANSWERS
  // -----------------------------
  const fetchAllReports = async () => {
    console.log(
      "📁 Fetching allReports for sessionId/themeId:",
      sessionId,
      themeId
    );

    const q = query(
      collection(database, "allReports"),
      where("sessionId", "==", sessionId),
      where("themeId", "==", themeId)
    );

    const snap = await getDocs(q);
    const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    console.log("📄 Total responses fetched:", data.length);

    return data;
  };

  // -----------------------------
  // BUILD AGGREGATES
  // -----------------------------
  const buildAggregates = (responses, theme) => {
    const questionCount =
      theme?.questions?.length ??
      Math.max(...responses.map((r) => Number(r.currentQueston || 0))) + 1;

    console.log("🧮 Total Questions:", questionCount);

    const result = [];

    for (let qi = 0; qi < questionCount; qi++) {
      const question =
        theme?.questions?.[qi] || {
          text: `Question ${qi}`,
          options: []
        };

      const counts = {};
      question.options.forEach((_, i) => (counts[i] = 0));
      counts["100"] = 0;

      const resForQ = responses.filter(
        (r) => Number(r.currentQueston) === qi
      );

      resForQ.forEach((r) => {
        const opt = String(r.option);
        counts[opt] = (counts[opt] || 0) + 1;
      });

      const total = Object.values(counts).reduce((a, c) => a + c, 0);

      const percentages = {};
      Object.keys(counts).forEach((k) => {
        percentages[k] =
          total === 0 ? 0 : Math.round((counts[k] / total) * 100);
      });

      result.push({
        questionIndex: qi,
        questionText: question.text,
        options: question.options,
        counts,
        percentages,
        total
      });
    }

    console.log("🧮 Aggregates:", result);
    return result;
  };

  // -----------------------------
  // EXPORT WORKBOOK
  // -----------------------------
  const exportWorkbook = (responses, theme, aggregates) => {
    if (!sessionId || !themeId) {
      console.error("❌ sessionId/themeId missing — cannot export file.");
      return;
    }

    const wb = XLSX.utils.book_new();

    // --- RAW RESPONSES ---
    const rawSheet = responses.map((r) => ({
      id: r.id,
      name: r.name,
      userId: r.userId,
      email: r.email,
      question: r.currentQueston,
      option: r.option,
      timestamp: r.timestamp
        ? new Date(r.timestamp.seconds * 1000).toISOString()
        : ""
    }));

    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rawSheet),
      "allReports"
    );

    // --- QUESTIONS SHEET ---
    if (theme?.questions) {
      const qRows = theme.questions.map((q, i) => ({
        index: i,
        text: q.text,
        options: q.options.join(" | "),
        points: q.points ?? "",
        time: q.time ?? ""
      }));

      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(qRows),
        "questions"
      );
    }

    // --- PER QUESTION SHEETS ---
    aggregates.forEach((ag) => {
      const rows = responses
        .filter((r) => Number(r.currentQueston) === ag.questionIndex)
        .map((r) => ({
          name: r.name,
          userId: r.userId,
          option: r.option,
          timestamp: r.timestamp
            ? new Date(r.timestamp.seconds * 1000).toISOString()
            : ""
        }));

      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        `Q${ag.questionIndex}`
      );
    });

    // --- AGGREGATES SHEET ---
    const aggRows = [];
    aggregates.forEach((ag) => {
      ag.options.forEach((opt, i) => {
        aggRows.push({
          question: ag.questionIndex,
          text: ag.questionText,
          optionIndex: i,
          optionText: opt,
          count: ag.counts[i],
          percentage: ag.percentages[i],
          total: ag.total
        });
      });

      if (ag.counts["100"] > 0) {
        aggRows.push({
          question: ag.questionIndex,
          text: ag.questionText,
          optionIndex: 100,
          optionText: "Other",
          count: ag.counts["100"],
          percentage: ag.percentages["100"],
          total: ag.total
        });
      }
    });

    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(aggRows),
      "aggregates"
    );

    // --- FINAL SAVE ---
    const fileName = `report_${sessionId}_${themeId}.xlsx`;
    console.log("📤 Saving File:", fileName);

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      fileName
    );
  };

  // -----------------------------
  // MAIN ACTION
  // -----------------------------
  const handleExport = async () => {
    setLoading(true);
    setError(null);

    console.log("🚀 Export Triggered");

    if (!sessionId || !themeId) {
      setError("❌ sessionId or themeId missing in URL.");
      return setLoading(false);
    }

    try {
      const theme = await fetchTheme();
      if (!theme) {
        setError("❌ Theme NOT found in Firestore.");
        return setLoading(false);
      }

      const responses = await fetchAllReports();
      if (!responses || responses.length === 0) {
        setError("❌ No responses found for this session/theme.");
        return setLoading(false);
      }

      const aggregates = buildAggregates(responses, theme);

      exportWorkbook(responses, theme, aggregates);
    } catch (err) {
      console.error("❌ Export failed:", err);
      setError(err.message);
    }

    setLoading(false);
  };

  // -----------------------------
  // UI
  // -----------------------------
  return (
    <div style={{ padding: 20 }}>
      <h2>Export Reports</h2>

      <button onClick={handleExport} disabled={loading}>
        {loading ? "Preparing..." : "Download Excel"}
      </button>

      {error && <div style={{ color: "red", marginTop: 15 }}>{error}</div>}
    </div>
  );
}
